import React, { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ProgressBar from "../../../../UI/ProgressBar";
import { useGetCompanyUserSummaryQuery } from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { Error404 } from "../../../../UI/404/Error404";
import { SortIcon } from "../../../Manage Organization/Employee/EmployeeTable";
import emptyStateSearch from "../../../../../assets/employee/empty_state_search.svg";
import { ReportsTruncatedName } from "../../ReportsTruncatedName";

export const UserSummaryTable = ({
  check,
  searchQuery = "",
  getTableData,
  startDate,
  endDate,
}) => {
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState("asc");
  const { data, isError, isLoading, isFetching } =
    useGetCompanyUserSummaryQuery(
      {
        sortBy: sortBy,
        type: sortType,
        query: searchQuery,
        startDate,
        endDate,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const handleSorting = (sortKey) => {
    setSortBy(sortKey);
    setSortType(!sortType || sortType === "desc" ? "asc" : "desc");
  };

  const sortedRows = useMemo(() => {
    const rows = [...(data?.data || [])];
    if (!sortBy) return rows;

    const getValue = (row) => {
      if (sortBy === "company_name") return row?.company?.name || "";
      if (sortBy === "lastLogin")
        return row?.lastLogin ? new Date(row.lastLogin).getTime() : 0;
      if (sortBy === "totalPointsEarned")
        return Number(row?.totalPointsEarned || 0);
      return row?.[sortBy] ?? "";
    };

    rows.sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortType === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortType === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return rows;
  }, [data, sortBy, sortType]);
  const allFieldsDeselected = Object.values(check).every((v) => !v);

  React.useEffect(() => {
    if (!data) return;
    getTableData && getTableData({ ...data, data: sortedRows });
  }, [data, sortedRows, getTableData]);

  React.useEffect(() => {}, [check]);
  if (isLoading || isFetching) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  return (
    <div
      style={
        allFieldsDeselected
          ? { overflow: "hidden", maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <TableContainer
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 270px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflow: allFieldsDeselected ? "hidden" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={
            allFieldsDeselected
              ? { tableLayout: "fixed", width: "100%", minWidth: 0 }
              : { minWidth: 2000 }
          }
        >
          <TableHead>
            <TableRow className="tableUi__head">
              {allFieldsDeselected ? (
                <TableCell
                  sx={{ borderRadius: "20px 20px 0px 0px" }}
                  align="center"
                >
                  Select fields to view data
                </TableCell>
              ) : (
                <>
                  {check.depName && (
                    <TableCell
                      sx={{ borderRadius: "20px 0px 0px 0px" }}
                      align="center"
                      onClick={() => handleSorting("company_name")}
                    >
                      <div className="tableUi__head_sortable">
                        Company Name <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.hod && (
                    <TableCell align="center">Company Admin</TableCell>
                  )}
                  {check.empName && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("fname")}
                    >
                      <div className="tableUi__head_sortable">
                        Employee Name <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.email && (
                    <TableCell align="center">Employee Email</TableCell>
                  )}
                  {check.empId && (
                    <TableCell align="center">Employee ID</TableCell>
                  )}
                  {check.lLogin && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("lastLogin")}
                    >
                      <div className="tableUi__head_sortable">
                        Last login <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.booksComp && (
                    <TableCell align="center">Books Completed</TableCell>
                  )}
                  {check.bookInProg && (
                    <TableCell align="center">Book In Progress</TableCell>
                  )}
                  {check.totalPointsEarned && (
                    <TableCell
                      sx={{ borderRadius: "0px 20px 0px 0px" }}
                      align="center"
                      onClick={() => handleSorting("totalPointsEarned")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Points Earned <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {allFieldsDeselected ? (
              <TableRow>
                <TableCell
                  colSpan={1}
                  align="center"
                  sx={{
                    border: "none",
                    padding: "40px 20px",
                    verticalAlign: "middle",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                    }}
                  >
                    <img
                      src={emptyStateSearch}
                      alt="No data"
                      style={{
                        width: "220px",
                        height: "165px",
                        marginTop: "150px",
                      }}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ) : searchQuery && (!data?.data || data?.data.length === 0) ? (
              <TableRow>
                <TableCell
                  colSpan={Object.values(check).filter(Boolean).length || 8}
                  align="center"
                  sx={{
                    border: "none",
                    padding: "60px 20px",
                    height: "calc(100vh - 320px)",
                    verticalAlign: "middle",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "20px",
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      maxWidth: "100vw",
                    }}
                  >
                    <img
                      src={emptyStateSearch}
                      alt="No search results"
                      style={{
                        width: "220px",
                        height: "165px",
                        paddingTop: "40px",
                      }}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <>
                  <TableRow
                    className="tableUi___body"
                    key={index}
                  >
                    {check.depName && (
                      <TableCell align="center">
                        <ReportsTruncatedName
                          value={row?.company && row.company.name}
                        />
                      </TableCell>
                    )}
                    {check.hod && (
                      <TableCell align="center">
                        {row?.company && row.company.companyAdmin?.email}
                      </TableCell>
                    )}
                    {check.empName && (
                      <TableCell align="center">
                        {row?.fname + " " + row.lname}
                      </TableCell>
                    )}
                    {check.email && (
                      <TableCell align="center">{row?.email}</TableCell>
                    )}
                    {check.empId && (
                      <TableCell align="center">{row?.employeeId}</TableCell>
                    )}
                    {check.lLogin && (
                      <TableCell align="center">
                        {row?.lastLogin && row.lastLogin.slice(0, 10)}
                      </TableCell>
                    )}
                    {check.booksComp && (
                      <TableCell align="center">
                        {row?.booksCompleted}
                      </TableCell>
                    )}
                    {check.bookInProg && (
                      <TableCell align="center">
                        {row?.booksInProgress}
                      </TableCell>
                    )}

                    {check.totalPointsEarned && (
                      <TableCell align="center">
                        {row?.totalPointsEarned}
                      </TableCell>
                    )}
                  </TableRow>
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
